import React, { useEffect, useState } from 'react';
import {
  Ticket, Plus, Copy, CheckCircle2, XCircle, ChevronDown, ChevronRight,
  Loader2, AlertTriangle, Power, PowerOff, Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../services/api';

const TIERS = [
  { value: 'investor',      label: 'Investor (£99.99/mo)' },
  { value: 'auction_house', label: 'Auction House (£49.99/mo)' },
  { value: 'estate_agent',  label: 'Estate Agent (£49.99/mo)' },
  { value: 'valuer',        label: 'Valuer (£49.99/mo)' },
  { value: 'deal_source',   label: 'Deal Source' },
  { value: 'white_label',   label: 'White Label' },
  { value: 'all',           label: 'All roles' },
];

const DURATION_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'lifetime', label: 'Lifetime' },
];

const PRESET_DAYS = [7, 14, 21, 28];

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [showCreator, setShowCreator] = useState(false);

  const [form, setForm] = useState({
    promo_kind: 'trial',
    trial_days: 7,
    is_lifetime: false,
    discount_percent: 20,
    discount_duration_value: 1,
    discount_duration_unit: 'months',
    custom_code: '',
    applies_to_tiers: ['investor'],
    max_uses: '',
    notes: '',
  });

  const toggleTier = (value, checked) => {
    setForm(f => {
      if (value === 'all') {
        return { ...f, applies_to_tiers: checked ? ['all'] : [] };
      }
      const withoutAll = f.applies_to_tiers.filter(x => x !== 'all');
      return {
        ...f,
        applies_to_tiers: checked
          ? [...withoutAll, value]
          : withoutAll.filter(x => x !== value),
      };
    });
  };

  const applyAllDiscountPreset = () => {
    setForm(f => ({
      ...f,
      promo_kind: 'discount',
      applies_to_tiers: ['all'],
      discount_percent: 20,
      discount_duration_value: 1,
      discount_duration_unit: 'months',
      is_lifetime: false,
    }));
  };

  const refresh = () => {
    const params = new URLSearchParams();
    if (!showInactive) params.set('only_active', 'true');
    authFetch(`/api/admin/promo-codes?${params}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { setCodes(d); setError(null); })
      .catch(e => setError(e.message));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [showInactive]);

  const toggleExpanded = (id) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (form.applies_to_tiers.length === 0) {
      toast.error('Pick at least one tier');
      return;
    }
    if (form.promo_kind === 'discount' && form.discount_duration_unit !== 'lifetime' && !form.discount_duration_value) {
      toast.error('Set a discount duration');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        promo_kind: form.promo_kind,
        trial_days: form.is_lifetime ? 0 : Number(form.trial_days),
        is_lifetime: form.is_lifetime,
        custom_code: form.custom_code.trim() || null,
        applies_to_tiers: form.applies_to_tiers,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        notes: form.notes || null,
      };
      if (form.promo_kind === 'discount') {
        payload.discount_percent = Number(form.discount_percent);
        payload.discount_duration_unit = form.discount_duration_unit;
        if (form.discount_duration_unit !== 'lifetime') {
          payload.discount_duration_value = Number(form.discount_duration_value);
        }
      }
      const r = await authFetch('/api/admin/promo-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      const created = await r.json();
      toast.success(`Created ${created.code}`);
      copyCode(created.code);
      setShowCreator(false);
      setForm({
        promo_kind: 'trial',
        trial_days: 7, is_lifetime: false,
        discount_percent: 20, discount_duration_value: 1, discount_duration_unit: 'months',
        custom_code: '',
        applies_to_tiers: ['investor'], max_uses: '', notes: '',
      });
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const setActive = async (code, deactivate) => {
    const path = deactivate ? 'deactivate' : 'reactivate';
    try {
      const r = await authFetch(`/api/admin/promo-codes/${code.id}/${path}`, {
        method: 'PATCH',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(deactivate ? `Deactivated ${code.code}` : `Reactivated ${code.code}`);
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const extendRedemption = async (redemption) => {
    const days = window.prompt('Extend trial by how many days?', '7');
    if (!days || isNaN(Number(days))) return;
    try {
      const r = await authFetch(`/api/admin/promo-codes/redemptions/${redemption.id}/extend`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extra_days: Number(days) }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success(`Extended trial for ${redemption.user_email} by ${days} days`);
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Ticket size={22} className="text-emerald-400" /> Promo / Demo Codes
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Create trial codes or membership discount codes. Users redeem each code once on their account;
            discounts apply automatically at Stripe checkout.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreator(s => !s)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold rounded-xl"
        >
          <Plus size={16} /> {showCreator ? 'Cancel' : 'New code'}
        </button>
      </div>

      {showCreator && (
        <form
          onSubmit={submitCreate}
          className="p-5 bg-slate-900 border border-slate-800 rounded-2xl space-y-4"
        >
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold mr-2">Code type</span>
            {[
              { value: 'trial', label: 'Free trial' },
              { value: 'discount', label: 'Membership discount (% off)' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm(f => ({ ...f, promo_kind: opt.value, is_lifetime: false }))}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                  form.promo_kind === opt.value
                    ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
            <button
              type="button"
              onClick={applyAllDiscountPreset}
              className="ml-auto px-3 py-2 rounded-lg text-sm font-medium bg-violet-500/10 border border-violet-500/30 text-violet-300 hover:bg-violet-500/20"
            >
              Quick: All (% off)
            </button>
          </div>

          {form.promo_kind === 'trial' && (
          <label className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_lifetime}
              onChange={e => setForm(f => ({ ...f, is_lifetime: e.target.checked }))}
              className="mt-0.5 w-4 h-4 accent-amber-500"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-200">Lifetime — never charged</div>
              <div className="text-xs text-amber-300/80 mt-0.5">
                Redemption permanently flags the user as never-charged. Hand this code out
                carefully — there's no expiry. Recommended to set max-uses = 1 for safety.
              </div>
            </div>
          </label>
          )}

          {form.promo_kind === 'discount' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded-xl bg-violet-500/5 border border-violet-500/20">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Discount %</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={form.discount_percent}
                  onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Duration</span>
                <input
                  type="number"
                  min="1"
                  max="3650"
                  disabled={form.discount_duration_unit === 'lifetime'}
                  value={form.discount_duration_value}
                  onChange={e => setForm(f => ({ ...f, discount_duration_value: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 disabled:opacity-40"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Unit</span>
                <select
                  value={form.discount_duration_unit}
                  onChange={e => setForm(f => ({ ...f, discount_duration_unit: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200"
                >
                  {DURATION_UNITS.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </label>
              <p className="md:col-span-3 text-xs text-slate-500">
                Discount applies at Stripe checkout when the user subscribes. Short periods may bill as at least one month in Stripe.
              </p>
            </div>
          )}

          {form.promo_kind === 'trial' && (
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <label className={`block ${form.is_lifetime ? 'opacity-40 pointer-events-none' : ''}`}>
              <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Trial length (days)</span>
              <div className="mt-1 flex gap-1.5">
                {PRESET_DAYS.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, trial_days: d }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                      form.trial_days === d
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {d}
                  </button>
                ))}
                <input
                  type="number"
                  min="1" max="365"
                  value={form.trial_days}
                  onChange={e => setForm(f => ({ ...f, trial_days: e.target.value }))}
                  className="w-20 px-2 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 text-center"
                />
              </div>
            </label>
          </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Max uses</span>
              <input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                placeholder="Unlimited"
                className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200"
              />
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Notes</span>
              <input
                type="text"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Launch promo May 2026"
                className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
              Custom code <span className="font-normal text-slate-500 normal-case">(optional — leave empty to auto-generate)</span>
            </span>
            <input
              type="text"
              value={form.custom_code}
              onChange={e => setForm(f => ({ ...f, custom_code: e.target.value.toUpperCase() }))}
              placeholder="e.g. ADMININT152"
              maxLength={40}
              pattern="[A-Za-z0-9-]{6,40}"
              className="mt-1 w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-emerald-300 font-mono uppercase"
            />
            <span className="text-[10px] text-slate-500 mt-1 block">
              6–40 chars, letters / digits / dashes only.
            </span>
          </label>

          <div>
            <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Applies to</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {TIERS.map(t => (
                <label
                  key={t.value}
                  className={`px-3 py-2 rounded-lg text-sm cursor-pointer border ${
                    form.applies_to_tiers.includes(t.value)
                      ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.applies_to_tiers.includes(t.value)}
                    onChange={(e) => toggleTier(t.value, e.target.checked)}
                    className="hidden"
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowCreator(false)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-sm font-semibold rounded-xl"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create &amp; copy code
            </button>
          </div>
        </form>
      )}

      <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={e => setShowInactive(e.target.checked)}
          className="w-4 h-4 accent-emerald-500"
        />
        Show deactivated / exhausted codes
      </label>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {!codes && !error && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}

      {codes && codes.length === 0 && (
        <p className="text-slate-500 text-sm">No codes yet. Create one above.</p>
      )}

      {codes && codes.length > 0 && (
        <div className="space-y-2">
          {codes.map(c => {
            const isOpen = expanded.has(c.id);
            return (
              <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(c.id)}
                    className="text-slate-500 hover:text-slate-300"
                  >
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>

                  <code className="text-sm font-mono px-2.5 py-1 bg-slate-800 text-emerald-300 rounded-md select-all">
                    {c.code}
                  </code>

                  <button
                    type="button"
                    onClick={() => copyCode(c.code)}
                    className="text-slate-400 hover:text-slate-200"
                    title="Copy code"
                  >
                    <Copy size={14} />
                  </button>

                  <span className="text-slate-300 text-sm flex items-center gap-1">
                    {c.promo_kind === 'discount' ? (
                      <span className="px-2 py-0.5 rounded-md text-xs bg-violet-500/15 text-violet-300 font-semibold">
                        {c.discount_percent}% off
                        {c.discount_is_lifetime ? ' · lifetime' : c.discount_duration_days ? ` · ${c.discount_duration_days}d` : ''}
                      </span>
                    ) : c.is_lifetime ? (
                      <span className="px-2 py-0.5 rounded-md text-xs bg-amber-500/15 text-amber-300 font-semibold uppercase tracking-wide">
                        Lifetime
                      </span>
                    ) : (
                      <>
                        <Clock size={13} className="text-slate-500" /> {c.trial_days}d trial
                      </>
                    )}
                  </span>

                  <span className="text-xs text-slate-500">
                    {c.applies_to_tiers.map(t => (t === 'all' ? 'All' : t.replace('_', ' '))).join(' · ')}
                  </span>

                  <span className="ml-auto text-xs text-slate-400 tabular-nums">
                    {c.uses_count}{c.max_uses ? ` / ${c.max_uses}` : ''} uses
                  </span>

                  {c.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-emerald-500/10 text-emerald-400">
                      <CheckCircle2 size={12} /> active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-slate-800 text-slate-500">
                      <XCircle size={12} /> {c.deactivated_at ? 'deactivated' : 'exhausted'}
                    </span>
                  )}

                  {c.deactivated_at ? (
                    <button
                      type="button"
                      onClick={() => setActive(c, false)}
                      className="text-emerald-400 hover:text-emerald-300"
                      title="Reactivate"
                    >
                      <Power size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActive(c, true)}
                      className="text-slate-500 hover:text-rose-400"
                      title="Deactivate"
                    >
                      <PowerOff size={14} />
                    </button>
                  )}
                </div>

                {c.notes && (
                  <p className="px-12 pb-3 text-xs text-slate-500 italic">{c.notes}</p>
                )}

                {isOpen && (
                  <div className="border-t border-slate-800 bg-slate-950/50 px-12 py-3">
                    {c.redemptions.length === 0 ? (
                      <p className="text-xs text-slate-500">Not yet redeemed.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="text-slate-500 text-[10px] uppercase tracking-wide">
                          <tr>
                            <th className="text-left py-1 font-medium">User</th>
                            <th className="text-left py-1 font-medium">Redeemed</th>
                            <th className="text-left py-1 font-medium">Trial ends</th>
                            <th className="text-right py-1 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                          {c.redemptions.map(r => (
                            <tr key={r.id}>
                              <td className="py-2 text-slate-300">{r.user_email}</td>
                              <td className="py-2 text-slate-400">{fmtDate(r.redeemed_at)}</td>
                              <td className="py-2 text-slate-400">
                                {fmtDate(r.trial_ends_at)}
                                {r.extended_to_at && (
                                  <span className="ml-2 text-[10px] text-emerald-400">extended</span>
                                )}
                              </td>
                              <td className="py-2 text-right">
                                {c.promo_kind !== 'discount' && (
                                <button
                                  type="button"
                                  onClick={() => extendRedemption(r)}
                                  className="text-xs px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300"
                                >
                                  Extend
                                </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
